import React, { useRef } from 'react';
import { ImageBlock as ImageBlockType } from './types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ImageIcon, Upload, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNotify } from '@/hooks/useNotify';

interface ImageBlockProps {
  block: ImageBlockType;
  onChange: (block: ImageBlockType) => void;
  isEditing?: boolean;
}

export const ImageBlockDisplay: React.FC<{ block: ImageBlockType }> = ({ block }) => {
  if (!block.src) {
    return (
      <div 
        className="flex items-center justify-center bg-muted/50 border-2 border-dashed border-muted-foreground/30 rounded"
        style={{ width: block.width, height: block.height || 80 }}
      >
        <ImageIcon className="h-8 w-8 text-muted-foreground/50" />
      </div>
    );
  }

  return (
    <img
      src={block.src}
      alt=""
      style={{
        width: block.width,
        height: block.height || 'auto',
        borderRadius: block.borderRadius,
        display: 'block',
      }}
    />
  );
};

export const ImageBlockEditor: React.FC<ImageBlockProps> = ({ block, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const notify = useNotify();

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const fileExt = file.name.split('.').pop();
      const filePath = `signatures/${user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('signature-assets')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('signature-assets')
        .getPublicUrl(filePath);

      onChange({ ...block, src: publicUrl });
      
      notify.success("Image uploaded");
    } catch (error: any) {
      notify.error(error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Image</Label>
        {block.src ? (
          <div className="relative inline-block">
            <img 
              src={block.src} 
              alt="" 
              className="max-w-[200px] rounded border"
            />
            <Button
              variant="destructive"
              size="icon"
              className="absolute -top-2 -right-2 h-6 w-6"
              onClick={() => onChange({ ...block, src: '' })}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileUpload}
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Image
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>Or paste image URL</Label>
        <Input
          value={block.src}
          onChange={(e) => onChange({ ...block, src: e.target.value })}
          placeholder="https://..."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Width (px)</Label>
          <Input
            type="number"
            value={block.width}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') onChange({ ...block, width: '' as any });
              else { const num = parseInt(val); if (!isNaN(num)) onChange({ ...block, width: num }); }
            }}
            onBlur={(e) => {
              const num = parseInt(e.target.value);
              if (isNaN(num) || num < 1) onChange({ ...block, width: 100 });
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>Height (px, optional)</Label>
          <Input
            type="number"
            value={block.height || ''}
            onChange={(e) => onChange({ ...block, height: e.target.value ? parseInt(e.target.value) : undefined })}
            placeholder="Auto"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Border Radius (px)</Label>
        <Input
          type="number"
          value={block.borderRadius || 0}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '') onChange({ ...block, borderRadius: '' as any });
            else { const num = parseInt(val); if (!isNaN(num)) onChange({ ...block, borderRadius: num }); }
          }}
          onBlur={(e) => {
            const num = parseInt(e.target.value);
            if (isNaN(num)) onChange({ ...block, borderRadius: 0 });
          }}
        />
      </div>

      <div className="space-y-2">
        <Label>Link URL (optional)</Label>
        <Input
          value={block.link || ''}
          onChange={(e) => onChange({ ...block, link: e.target.value || undefined })}
          placeholder="https://..."
        />
      </div>
    </div>
  );
};
